'use client';
import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import { ArrowLeftIcon, ArrowRightIcon } from '@sanity/icons';
import { Button } from '@ui/Button/Button';
import cn from '@utils/cn';
import { DEFAULT_AUTO_PLAY_DELAY } from './TestimonialsSlider';

type Props = {
  testimonials: React.ReactElement[];
  autoplayDelay?: number;
};

export default function TestimonialsSliderClient({ testimonials, autoplayDelay = DEFAULT_AUTO_PLAY_DELAY }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center', containScroll: false }, [
    Autoplay({ playOnInit: true, delay: autoplayDelay * 1000 }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    emblaApi?.on('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="m-auto w-full overflow-hidden" ref={emblaRef}>
        <div className="flex">{testimonials}</div>
      </div>
      <div className="flex w-full flex-col items-center gap-8">
        <div className="flex w-full justify-center gap-4">
          {testimonials.map((_t, i) => (
            <span
              key={i}
              className={cn('bg-tertiary h-1 w-8 rounded-md', {
                'bg-tertiary-foreground': i === selectedIndex,
              })}
            ></span>
          ))}
        </div>

        <div className="flex w-full justify-center gap-4">
          <>
            <Button
              className="hover:border-tertiary-foreground border-0 hover:border hover:bg-inherit"
              size="icon"
              variant="outline"
              aria-label="Previous"
              onClick={() => emblaApi?.scrollPrev()}
            >
              <ArrowLeftIcon width={24} height={24} className="text-foreground" />
            </Button>
            <Button
              className="hover:border-tertiary-foreground border-0 hover:border hover:bg-inherit"
              size="icon"
              variant="outline"
              aria-label="Next"
              onClick={() => emblaApi?.scrollNext()}
            >
              <ArrowRightIcon width={24} height={24} className="text-foreground" />
            </Button>
          </>
        </div>
      </div>
    </div>
  );
}
